import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Tag } from "primereact/tag";
import FinancialAccountService from "../../services/FinancialAccountService";

const accountStyles = {
  1: {
    icon: "pi pi-money-bill",
    label: "Kasa",
    card: "!bg-emerald-50 border-emerald-200",
    accent: "bg-emerald-500",
    iconShadow: "shadow-emerald-600/30",
    tag: "!bg-emerald-100 text-emerald-700 border-emerald-200",
    hoverTitle: "group-hover:text-emerald-700",
  },
  2: {
    icon: "pi pi-building",
    label: "Banka",
    card: "!bg-blue-50 border-blue-200",
    accent: "!bg-blue-500",
    iconShadow: "shadow-blue-600/30",
    tag: "!bg-blue-100 text-blue-700 border-blue-200",
    hoverTitle: "group-hover:text-blue-700",
  },
  3: {
    icon: "pi pi-credit-card",
    label: "Kredi Kartı",
    card: "!bg-orange-50 border-orange-200",
    accent: "!bg-orange-500",
    iconShadow: "shadow-orange-600/30",
    tag: "!bg-orange-100 text-orange-700 border-orange-200",
    hoverTitle: "group-hover:text-orange-700",
  },
  default: {
    icon: "pi pi-wallet",
    label: "Diğer",
    card: "!bg-slate-50 border-slate-200",
    accent: "!bg-slate-500",
    iconShadow: "shadow-slate-600/30",
    tag: "!bg-slate-100 text-slate-700 border-slate-200",
    hoverTitle: "group-hover:text-slate-700",
  },
};

export default function FinancialAccountsListPage() {
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const toast = useRef(null);

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      setLoading(true);

      const res = await FinancialAccountService.getAll();

      if (res.success) {
        setAccounts(res.data || []);
      }
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "Hesaplar yüklenirken hata oluştu",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStyle = (type) => accountStyles[type] || accountStyles.default;

  return (
    <div className="p-4 md:p-6 space-y-6 animate-fade-in">
      <Toast ref={toast}  appendTo={mounted ? document.body : "self"} />

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="bg-slate-900 p-4 rounded-2xl shadow-xl shadow-slate-900/20">
            <i className="pi pi-wallet text-white text-2xl" />
          </div>

          <div>
            <h1 className="text-2xl font-black text-slate-800 m-0 uppercase tracking-tight">
              Hesaplarım
            </h1>
            <p className="text-sm text-slate-500 font-medium m-0">
              Kasa, banka ve kredi kartı bakiyeleriniz
            </p>
          </div>
        </div>

        <Button
          label="Yeni Hesap Ekle"
          icon="pi pi-plus"
          className="!bg-emerald-600 !border-none rounded-xl font-black px-6 py-3 shadow-lg shadow-emerald-600/20"
          onClick={() => navigate("/finance/accounts/add")}
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="pi pi-spin pi-spinner text-4xl text-slate-300" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {accounts.map((acc) => {
            const style = getStyle(acc.accountType);

            return (
              <div
                key={acc.id}
                onClick={() => navigate(`/finance/accounts/detail/${acc.id}`)}
                className={`group relative ${style.card} border rounded-[2rem] p-6 shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 cursor-pointer overflow-hidden`}
              >
                <div
                  className={`absolute top-0 right-0 w-32 h-32 -mr-8 -mt-8 ${style.accent} rounded-full opacity-90 group-hover:scale-150 transition-transform duration-700`}
                />

                <div className="relative z-10 flex flex-col h-full">
                  <div className="flex justify-between items-start mb-6">
                    <div
                      className={`w-6 h-12 rounded-2xl ${style.accent} flex items-center justify-center shadow-lg ${style.iconShadow}`}
                    >
                      <i className={`${style.icon} text-white text-xl`} />
                    </div>

                    <Tag
                      value={style.label}
                      className={`${style.tag} text-[14px] font-black uppercase px-3 py-1 rounded-full border`}
                    />
                  </div>

                  <div className="flex flex-col gap-1 mb-6">
                    <span className="text-slate-400 text-[10px] font-black uppercase tracking-widest">
                      {acc.accountNumber || "HESAP NO YOK"}
                    </span>

                    <h2
                      className={`text-lg font-black text-slate-800 leading-tight transition-colors ${style.hoverTitle}`}
                    >
                      {acc.name}
                    </h2>
                  </div>

                  <div className="mt-auto pt-6 border-t border-white/60 flex flex-col">
                    <span className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1">
                      MEVCUT BAKİYE
                    </span>

                    <div className="flex items-baseline gap-1">
                      <span
                        className={`text-2xl font-black ${
                          acc.balance >= 0 ? "text-slate-800" : "text-red-600"
                        }`}
                      >
                        {acc.balance?.toLocaleString("tr-TR", {
                          minimumFractionDigits: 2,
                        })}
                      </span>

                      <span className="text-sm font-bold text-slate-400">
                        {acc.currencyCode}
                      </span>
                    </div>
                  </div>
                </div>

                <Button
                  icon="pi pi-pencil"
                  className="absolute top-4 right-4 p-button-rounded p-button-text p-button-secondary opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/finance/accounts/edit/${acc.id}`);
                  }}
                />
              </div>
            );
          })}

          {accounts.length === 0 && (
            <div className="col-span-full bg-slate-50 border-2 border-dashed border-slate-200 rounded-[2rem] p-12 flex flex-col items-center justify-center text-slate-400 gap-4">
              <i className="pi pi-wallet text-6xl opacity-20" />

              <div className="text-center">
                <p className="font-bold text-lg m-0 text-slate-500">
                  Henüz hesap tanımlanmamış
                </p>
                <p className="text-sm font-medium m-0">
                  Finansal işlemlerinizi takip etmek için ilk hesabınızı ekleyin.
                </p>
              </div>

              <Button
                label="Şimdi Ekle"
                icon="pi pi-plus"
                className="p-button-outlined p-button-secondary rounded-xl font-bold mt-2"
                onClick={() => navigate("/finance/accounts/add")}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}